import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useUser, AUTH_ENABLED } from '../lib/auth';
import { ProfileForm } from '../components/profiles/ProfileForm';
import type { ProfileFormData } from '../components/profiles/ProfileForm';
import { useProfiles } from '../hooks/useProfiles';
import { useUserSettings } from '../hooks/useUserSettings';
import { fmtDollarInt } from '../lib/format';
import { apiFetch } from '../lib/api';
import type { PendingInvitation } from '../lib/types';
import { ProfileListSkeleton } from '../components/ui/Skeletons';

function ProfileListContent() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const { profiles, loading, error, createProfile, fetchProfiles } = useProfiles(getToken);
  const { defaultProfileId } = useUserSettings(getToken);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Pending invitations
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [accepting, setAccepting] = useState<string | null>(null);

  const userEmail = user?.primaryEmailAddress?.emailAddress;

  const fetchInvitations = useCallback(async () => {
    if (!userEmail) return;
    setInvitesLoading(true);
    try {
      const data = await apiFetch<PendingInvitation[]>(
        `/invitations/pending?email=${encodeURIComponent(userEmail)}`,
        {},
        getToken,
      );
      setPendingInvites(data);
    } catch {
      // Non-fatal — invites section just stays empty
    } finally {
      setInvitesLoading(false);
    }
  }, [userEmail, getToken]);

  useEffect(() => { fetchInvitations(); }, [fetchInvitations]);

  const handleAccept = async (invite: PendingInvitation) => {
    if (!userEmail) return;
    setAccepting(invite.profileId);
    try {
      await apiFetch('/invitations/accept', {
        method: 'POST',
        body: JSON.stringify({ profileId: invite.profileId, email: userEmail, name: user?.fullName || '' }),
      }, getToken);
      setPendingInvites(prev => prev.filter(i => i.profileId !== invite.profileId));
      await fetchProfiles();
    } catch (err: any) {
      alert(`Failed to accept invitation: ${err.message}`);
    } finally {
      setAccepting(null);
    }
  };

  const handleCreate = async (data: ProfileFormData) => {
    try {
      setSaving(true);
      const profile = await createProfile({
        ...data,
        userEmail: user?.primaryEmailAddress?.emailAddress || '',
        userName: user?.fullName || user?.firstName || '',
      });
      setShowCreate(false);
      navigate(`/profiles/${profile.id}`);
    } catch (err: any) {
      alert(`Failed to create profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ProfileListSkeleton />;

  if (error) {
    return (
      <div className="page-container">
        <h2 className="page-title">Manage Profiles</h2>
        <div className="note-box" style={{ borderColor: 'var(--red)', background: 'rgba(239,68,68,0.1)' }}>
          Error loading profiles: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Link to="/dashboard" style={{ color: 'var(--primary)', fontSize: '0.85rem', textDecoration: 'none' }}>&larr; Dashboard</Link>

      <div className="page-header-row" style={{ marginTop: '0.75rem' }}>
        <div>
          <h2 className="page-title" style={{ marginBottom: '0.25rem' }}>Manage Profiles</h2>
          <p className="page-subtitle" style={{ margin: 0 }}>
            Create buyer profiles to save properties and track offer analyses.
          </p>
        </div>
        {profiles.length > 0 && !showCreate && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Profile
          </button>
        )}
      </div>

      {showCreate && (
        <div style={{ marginBottom: '1.5rem' }}>
          <ProfileForm
            title="Create Buyer Profile"
            submitLabel="Create Profile"
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
          />
        </div>
      )}

      {/* Pending Invitations */}
      {(invitesLoading || pendingInvites.length > 0) && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Pending Profile Invitations
          </div>
          <div className="card-body">
            {invitesLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Checking for invitations…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {pendingInvites.map(invite => (
                  <div key={invite.profileId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{invite.profileName}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        You've been invited to view this buyer profile
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', padding: '0.35rem 0.85rem' }}
                      disabled={accepting === invite.profileId}
                      onClick={() => handleAccept(invite)}
                    >
                      {accepting === invite.profileId ? 'Accepting…' : 'Accept'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {profiles.length === 0 && !showCreate ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏠</div>
          <div className="empty-state-text">No buyer profiles yet</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            A buyer profile stores your financial info so you can save properties and compare offers.
          </p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            Create Your First Profile
          </button>
        </div>
      ) : (
        <div className="card-grid">
          {profiles.map(profile => (
            <Link
              key={profile.id}
              to={`/profiles/${profile.id}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}>
                <div className="card-header">
                  {profile.name}
                  {profile.id === defaultProfileId && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--primary)', marginLeft: '0.5rem' }}>
                      Default
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                    <div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>Cash on Hand</div>
                      <div style={{ fontWeight: 600 }}>{fmtDollarInt(profile.cashOnHand)}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>Annual Income</div>
                      <div style={{ fontWeight: 600 }}>{fmtDollarInt(profile.grossAnnualIncome + (profile?.coBorrower?.grossAnnualIncome || 0))}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>Monthly Debts</div>
                      <div style={{ fontWeight: 600 }}>{fmtDollarInt(profile.monthlyDebts + (profile?.coBorrower?.monthlyDebts || 0))}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>Credit Score</div>
                      <div style={{ fontWeight: 600 }}>{profile.creditScoreRange}+</div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProfileListPage() {
  if (!AUTH_ENABLED) {
    return (
      <div className="page-container">
        <h2 className="page-title">Manage Profiles</h2>
        <div className="note-box">
          Sign in to create buyer profiles, save properties, and track offers.
          Configure <code>VITE_CLERK_PUBLISHABLE_KEY</code> to enable authentication.
        </div>
      </div>
    );
  }

  return <ProfileListContent />;
}
